import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject, throwError, EMPTY, firstValueFrom, from } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { environment } from "../../environments/environment";
import { RefreshTokenService } from "../services/refresh-token.service";
import { RevokeTokenService } from '../services/revoke-token.service';
import { Router, ActivatedRoute } from '@angular/router';
import { LogoutService } from "src/app/services/logout.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private API_URL = environment.api;
  private refreshingToken = false;
  private tokenSubject: Subject<string> = new Subject<string>();

  constructor(
    private refreshTokenService: RefreshTokenService,
    private revokeTokenService: RevokeTokenService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private logoutService: LogoutService
  ) { }

  checkCode(): Promise<boolean> {
    return new Promise(resolve => {
      this.activatedRoute.queryParams.subscribe(params => {
        if (params['code']) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  shouldIgnore(url: string): boolean {
    const ignoreUrls = [
      `${this.API_URL}security/token`,
      '/temp/docs',
      'security/public/token',
      'admin/account/config',
      'dipmal/dimension',
      '/dipmal/communications/contents/public',
      `/dipmal/campaign/config`,
      '/dipmal/donor/pre-restore-pwd',
      '/dipmal/communications/support',
      'diputacion.malaga.es/api/bnf/',
    ];
    return ignoreUrls.some(ignoreUrl => url.includes(ignoreUrl));
  }

  async revokeToken() {
    return firstValueFrom(this.revokeTokenService.revokeToken());
  }

  async logOut() {
    this.logoutService.logOut();
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.shouldIgnore(request.url)) {
      return next.handle(request);
    }

    return next.handle(this.addToken(request)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          if (request.url === `${this.API_URL}/protocol/openid-connect/logout`) {
            this.logOut();
            return EMPTY;
          } else {
            return from(this.checkCode()).pipe(
              switchMap(haveCode => {
                if (haveCode) {
                  sessionStorage.clear();
                  this.router.navigate(['/home']);
                  return EMPTY;
                } else {
                  return this.handle401Error(request, next);
                }
              })
            );
          }
        } else if (error.status === 403) {
          this.logOut();
          return EMPTY;
        } else {
          return throwError(() => error);
        }
      })
    );
  }


  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.refreshingToken) {
      this.refreshingToken = true;
      this.tokenSubject.next('');

      return this.refreshTokenService.getToken().pipe(
        switchMap(tokenResponse => {
          const accessToken = tokenResponse.access_token;
          sessionStorage.setItem('tokenLogIn', accessToken);
          this.refreshingToken = false;
          this.tokenSubject.next(accessToken);
          return next.handle(this.addToken(request));
        }),
        catchError(() => {
          this.refreshingToken = false;
          this.logOut();
          return EMPTY;
        })
      );
    } else {
      return this.tokenSubject.pipe(
        filter(token => token !== ''),
        take(1),
        switchMap(() => next.handle(this.addToken(request)))
      );
    }
  }

  private addToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = sessionStorage.getItem('tokenLogIn');
    const url = request.url;

    if (url.toString().includes('cloudfront.net/docs/')) {
      return request
    } else {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }

  }
}
